import React, { useEffect, useMemo, useState, useRef } from 'react'
import {useParams, useSearchParams} from 'react-router-dom';
import './searchObject.scss'
import AIPanel from "../../../components/AIChat/AIPanel";
import { useConvokeDomainContext } from '../ConvokeDomainProvider';
import {UnmanagedTableControl, IconControl, TabPaneControl} from 'spark-core-dx/components';
import {useCoreRoute, useGenericManager} from 'spark-core-dx/hooks'
import ReactECharts from 'echarts-for-react';
import html2canvas from 'html2canvas';
import { ItemServiceFactory } from '../../../services/ItemServiceFactory';
import { sanitizeHtmlFragment } from '../../../utilities/markupSanitizer';
import { resolveChatHistoryDisplayMode } from '../../../components/AIChat/chatOutputClassification';

function tryParseJson(input) {
    if (typeof input !== "string") {
        return null;
    }
    const trimmed = input.trim();
    if (!trimmed) {
        return null;
    }
    try {
        return JSON.parse(trimmed);
    } catch {
        return null;
    }
}

function parseMaybeJson(input) {
    if (typeof input === "string") {
        const parsed = tryParseJson(input);
        return parsed ?? input;
    }
    return input;
}

function extractEmbeddedComment(...values) {
    for (const value of values) {
        const parsed = parseMaybeJson(value);
        if (!parsed || typeof parsed !== "object") continue;

        const args = parsed?.args ?? parsed?.Args ?? parsed;
        const comment = String(
            args?.comment ??
            args?.Comment ??
            args?.message ??
            args?.Message ??
            parsed?.comment ??
            parsed?.Comment ??
            parsed?.message ??
            parsed?.Message ??
            ""
        ).trim();
        if (comment) {
            return comment;
        }
    }

    return "";
}

function getChatItemMessage(chatItem, fallbackValue = null) {
    const embeddedComment = extractEmbeddedComment(
        chatItem?.Value,
        chatItem?.value,
        chatItem?.Output,
        chatItem?.output,
        chatItem?.ResponseMessage,
        chatItem?.responseMessage,
        chatItem?.ModelResponse,
        chatItem?.modelResponse
    );
    if (embeddedComment) {
        return embeddedComment;
    }

    const candidates = [
        chatItem?.DisplayErrorMessage,
        chatItem?.displayErrorMessage,
        chatItem?.ResponseMessage,
        chatItem?.responseMessage,
        chatItem?.ModelResponse,
        chatItem?.modelResponse,
        typeof fallbackValue === "string" ? fallbackValue : ""
    ];

    for (const candidate of candidates) {
        const text = String(candidate ?? "").trim();
        if (text) {
            return text;
        }
    }

    return "";
}

const SearchWarehouse = ({
    panelMode = false,
    panelOpen = true,
    panelId = null,
    panelReferenceId = null,
    panelSessionId = null,
    onPanelClose = null
}) => {
    const DEBUG_HISTORY_MAX = 40;

    const [data, setData] = useState([]);
    const [uiMetadata, setUiMetadata] = useState([]);
    const [renderHtml, setRenderHtml] = useState("");
    const [chartPayload, setChartPayload] = useState(null);
    const [artifactCharts, setArtifactCharts] = useState([]);
    const [displayMode, setDisplayMode] = useState("chat");
    // Keep this enabled for now; convert to a user toggle later.
    const [showDebugPayload] = useState(true);
    const [debugPayload, setDebugPayload] = useState({
        uiCommand: null,
        item: null,
        value: null,
        receivedAt: null
    });
    const [debugHistory, setDebugHistory] = useState([]);
    const [debugSessionEntries, setDebugSessionEntries] = useState([]);
    const [selectedDebugEntryId, setSelectedDebugEntryId] = useState(null);
    const [lastCopiedKey, setLastCopiedKey] = useState(null);
    const [hasAskedQuestion, setHasAskedQuestion] = useState(false);
    const {coreNavigate} = useCoreRoute();
    const {loadingState = false } = useConvokeDomainContext() ?? {};
    const {wId: routeWId} = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const sidParamName = panelMode ? "aiPanelSessionId" : "sid";
    const getPanelText = (value) => String(value ?? "").trim();
    const wId = panelMode
        ? (getPanelText(panelId) || getPanelText(searchParams.get("aiPanelId")))
        : routeWId;
    const wdId = getPanelText(panelReferenceId) || getPanelText(searchParams.get(panelMode ? "aiPanelReferenceId" : "wdId"));
    const sid = getPanelText(panelSessionId) || getPanelText(searchParams.get(sidParamName));
    const debugHistoryStorageKey = useMemo(
        () => `search-warehouse-debug-history:${String(wdId ?? "none")}:${String(wId ?? "none")}`,
        [wdId, wId]
    );
    const pageRef = useRef(null);
    const outputPaneRef = useRef(null);
    const chartRef = useRef(null);
    const historyLoadStateRef = useRef({
        lastLoadedSessionId: null,
        inFlightSessionId: null
    });
    const [activeChatSessionId, setActiveChatSessionId] = useState(null);
    const [activeChatSessionItem, setActiveChatSessionItem] = useState(null);
    const [historyItemsByMode, setHistoryItemsByMode] = useState({
        render: [],
        data: [],
        chart: []
    });
    const [latestSessionActionItem, setLatestSessionActionItem] = useState(null);
    const [autoLoadedSessionId, setAutoLoadedSessionId] = useState(null);
    const [tabHistorySelection, setTabHistorySelection] = useState({
        result: "",
        data: ""
    });
    const [showHistoryHelp, setShowHistoryHelp] = useState(false);
    const [refreshInFlight, setRefreshInFlight] = useState(false);
    const [refreshError, setRefreshError] = useState("");
    const itemService = useMemo(() => new ItemServiceFactory(), []);
    const aiChatMgr = useGenericManager(itemService.ItemServiceEnum.AIChat, itemService);
    const urlSelectedSession = useMemo(() => {
        const sessionId = String(sid ?? "").trim();
        if (!sessionId) {
            return null;
        }

        return {
            _id: sessionId
        };
    }, [sid]);
    const selectedChatSession = useMemo(() => {
        if (!urlSelectedSession) {
            return activeChatSessionItem;
        }

        const activeSessionId = String(activeChatSessionItem?._id ?? "").trim();
        if (activeSessionId && activeSessionId === urlSelectedSession._id) {
            return activeChatSessionItem;
        }

        return urlSelectedSession;
    }, [activeChatSessionItem, urlSelectedSession]);
    const rows = Array.isArray(data) ? data : [];
    const sanitizedRenderHtml = useMemo(() => sanitizeHtmlFragment(renderHtml), [renderHtml]);
    const hasRows = rows.length > 0;
    const hasArtifactCharts = displayMode === "render" && Array.isArray(artifactCharts) && artifactCharts.length > 0;
    const hasRenderContent = displayMode === "render" && (String(sanitizedRenderHtml || "").trim().length > 0 || hasArtifactCharts);
    const hasChartContent = displayMode === "chart" && !!chartPayload;
    const isDataMode = displayMode === "data";
    const showTable = displayMode === "data" && hasRows;
    const hasDisplayableContent = showTable || hasRenderContent || hasChartContent;
    const activeOutputTabKey = displayMode === "data" ? "data" : "result";
    const latestWarehouseItem =
        (debugPayload?.item && typeof debugPayload.item === "object" ? debugPayload.item : null) ??
        latestSessionActionItem ??
        null;
    const latestWarehouseMessage = getChatItemMessage(
        latestWarehouseItem,
        typeof debugPayload?.value === "string" ? debugPayload.value : null
    );
    const hasResolvedWarehouseResult = !!latestWarehouseItem || !!debugPayload?.receivedAt;
    const indicatesNoWarehouseData = /no rows returned|returned zero rows|no results|no result|no data result|no chart:|cannot build .* no rows|did not return any rows|returned no rows/i.test(latestWarehouseMessage);
    const showLoadingState = hasAskedQuestion && loadingState && !hasDisplayableContent && !hasResolvedWarehouseResult;
    const showNoDataState = !showLoadingState && displayMode === "data" && !hasRows;
    const showNoRenderState = !showLoadingState && displayMode === "render" && !hasRenderContent;
    const showNoChartState = !showLoadingState && displayMode === "chart" && !hasChartContent;
    const showWarehouseNoResultsState =
        !showLoadingState &&
        displayMode === "chat" &&
        hasAskedQuestion &&
        !hasDisplayableContent &&
        indicatesNoWarehouseData;
    const showChatFallbackState =
        !showLoadingState &&
        !showWarehouseNoResultsState &&
        !showNoDataState &&
        !showNoRenderState &&
        !showNoChartState &&
        (!hasAskedQuestion || displayMode === "chat");
    const tableLoading = loadingState && !hasRows && !hasResolvedWarehouseResult;
    const emptyStateMessages = {
        warehouseNoResults: "The warehouse request completed, but no displayable results were returned.",
        data: "There is no table data to display right now. Try running the request again or asking for a different result.",
        render: "There is no result to display right now. Try running the request again or asking for a different result.",
        chart: "There is no chart to display right now. Try running the request again or asking for a different result."
    };
    const getHistoryDateValue = (chatItem) =>
        chatItem?.UpdatedDate ??
        chatItem?.updatedDate ??
        chatItem?.CreatedDate ??
        chatItem?.createdDate ??
        null;

    const getHistorySortTime = (chatItem) => {
        const value = getHistoryDateValue(chatItem);
        const time = value ? new Date(value).getTime() : 0;
        return Number.isNaN(time) ? 0 : time;
    };

    const formatHistoryLabel = (chatItem) => {
        const rawDate = getHistoryDateValue(chatItem);
        if (!rawDate) {
            const sequence = chatItem?.Sequence ?? "?";
            return `#${sequence}`;
        }
        const date = new Date(rawDate);
        if (Number.isNaN(date.getTime())) {
            const sequence = chatItem?.Sequence ?? "?";
            return `#${sequence}`;
        }
        return date.toLocaleString();
    };
    const tabHistoryOptions = useMemo(() => {
        const sortEntries = (entries) => entries.slice().sort((a, b) => {
            const aItem = a.item;
            const bItem = b.item;
            const aTime = getHistorySortTime(aItem);
            const bTime = getHistorySortTime(bItem);
            if (bTime !== aTime) return bTime - aTime;
            return (bItem?.Sequence ?? 0) - (aItem?.Sequence ?? 0);
        });
        const buildOptions = (entries, includeKindLabel = false) => {
            const options = [];
            for (const { mode, item: historyItem } of sortEntries(entries)) {
                const id = historyItem?._id ?? historyItem?.UniqueId;
                if (!id) continue;
                const kindLabel = mode === "chart" ? "Chart" : "Result";
                options.push({
                    value: `history:${mode}:${id}`,
                    label: includeKindLabel ? `${kindLabel} - ${formatHistoryLabel(historyItem)}` : formatHistoryLabel(historyItem)
                });
            }
            return options;
        };

        return {
            result: buildOptions(
                [
                    ...(historyItemsByMode.render || []).map((item) => ({ mode: "render", item })),
                    ...(historyItemsByMode.chart || []).map((item) => ({ mode: "chart", item }))
                ],
                true
            ),
            data: buildOptions((historyItemsByMode.data || []).map((item) => ({ mode: "data", item })))
        };
    }, [historyItemsByMode]);
    const activeTabHasHistory = (tabHistoryOptions[activeOutputTabKey] || []).length > 0;
    const hasActiveSession = !!activeChatSessionId;
    const effectiveDebugHistory = useMemo(() => {
        const merged = new Map();

        debugHistory.forEach((entry) => {
            if (!entry?.id) return;
            merged.set(entry.id, entry);
        });

        debugSessionEntries.forEach((entry) => {
            if (!entry?.id || merged.has(entry.id)) return;
            merged.set(entry.id, entry);
        });

        if (merged.size === 0 && debugPayload?.receivedAt) {
            merged.set("live-current", {
                id: "live-current",
                ...debugPayload
            });
        }

        return Array.from(merged.values()).sort((a, b) => {
            const aTime = new Date(a?.receivedAt ?? 0).getTime();
            const bTime = new Date(b?.receivedAt ?? 0).getTime();
            if (bTime !== aTime) return bTime - aTime;
            return String(a?.id ?? "").localeCompare(String(b?.id ?? ""));
        });
    }, [debugHistory, debugPayload, debugSessionEntries]);
    const selectedDebugPayload =
        effectiveDebugHistory.find((entry) => entry?.id === selectedDebugEntryId) ??
        effectiveDebugHistory[0] ??
        debugPayload;

    useEffect(() => {
        try {
            const raw = window.sessionStorage.getItem(debugHistoryStorageKey);
            if (!raw) {
                setDebugHistory([]);
                setSelectedDebugEntryId(null);
                return;
            }

            const parsed = JSON.parse(raw);
            const entries = Array.isArray(parsed)
                ? parsed.filter((entry) => entry && typeof entry === "object")
                : [];
            setDebugHistory(entries);
            setSelectedDebugEntryId(entries[0]?.id ?? null);
        } catch {
            setDebugHistory([]);
            setSelectedDebugEntryId(null);
        }
    }, [debugHistoryStorageKey]);

    const resolveRenderHtml = (value, item = null) => {
        const pickText = (candidate) => (typeof candidate === "string" ? candidate.trim() : "");
        const extractFromObject = (obj) => {
            if (!obj || typeof obj !== "object") return "";
            const direct =
                pickText(obj?.Html) ||
                pickText(obj?.html) ||
                pickText(obj?.ArtifactHtml) ||
                pickText(obj?.artifactHtml) ||
                pickText(obj?.RenderHtml) ||
                pickText(obj?.renderHtml) ||
                pickText(obj?.ModelResponse) ||
                pickText(obj?.modelResponse) ||
                pickText(obj?.ResponseMessage) ||
                pickText(obj?.responseMessage) ||
                "";
            if (direct) return direct;
            const nested = obj?.Output ?? obj?.output ?? obj?.Value ?? obj?.value ?? null;
            if (typeof nested === "string") return nested;
            if (nested && typeof nested === "object") return extractFromObject(nested);
            return "";
        };

        if (typeof value === "string") {
            const parsed = tryParseJson(value);
            if (parsed && typeof parsed === "object") {
                const parsedHtml = extractFromObject(parsed);
                if (parsedHtml) return parsedHtml;
            }
            return value;
        }

        const fromValueObject = extractFromObject(value);
        if (fromValueObject) return fromValueObject;

        const fromItem = extractFromObject(item);
        if (fromItem) return fromItem;

        return "";
    };

    const normalizeUiCommand = (input) => {
        const raw = String(input ?? "").trim().toLowerCase();
        if (raw === "display") return "data";
        return raw;
    };

    const isFailedChatItem = (chatItem) => {
        if (!chatItem || typeof chatItem !== "object") {
            return false;
        }

        if (chatItem?.IsError === true || chatItem?.isError === true) {
            return true;
        }

        const displayError = String(
            chatItem?.DisplayErrorMessage ??
            chatItem?.displayErrorMessage ??
            ""
        ).trim();
        if (displayError) {
            return true;
        }

        const outcome = String(
            chatItem?.AIOutcomeStatus ??
            chatItem?.aiOutcomeStatus ??
            ""
        ).trim().toLowerCase();

        return outcome === "failed";
    };

    const resolveItemUiCommand = (chatItem, fallback = null) => {
        return chatItem?.UICommand ?? chatItem?.uiCommand ?? chatItem?.UiCommand ?? fallback;
    };

    const resolveHistoryDisplayMode = (chatItem, fallback = null) => {
        return resolveChatHistoryDisplayMode(chatItem, resolveItemUiCommand(chatItem, fallback));
    };

    const resolveSessionId = (item, value) => {
        const itemSessionId = item?.ChatSessionId ?? item?.chatSessionId ?? item?.SessionId ?? item?.sessionId ?? null;
        const valueSessionId = value?.ChatSessionId ?? value?.chatSessionId ?? value?.SessionId ?? value?.sessionId ?? null;
        return itemSessionId || valueSessionId || null;
    };

    const resolvePreviewPayload = (value, item = null) => {
        const parsedValue = parseMaybeJson(value);
        const parsedItemOutput = parseMaybeJson(item?.Output ?? item?.output ?? item?.ModelResponse ?? item?.modelResponse ?? null);
        const candidates = [parsedValue, parsedItemOutput, value, item].filter(Boolean);

        for (const candidate of candidates) {
            if (!candidate || typeof candidate !== "object") continue;
            const direct = candidate.preview ?? candidate.Preview;
            if (direct && typeof direct === "object") return direct;
            const args = candidate.args ?? candidate.Args;
            const fromArgs = args?.preview ?? args?.Preview;
            if (fromArgs && typeof fromArgs === "object") return fromArgs;
            const finalUiCommand = String(candidate?.finalUiCommand ?? candidate?.FinalUiCommand ?? "").trim();
            const previewPayload =
                candidate?.payload ??
                candidate?.Payload ??
                candidate?.output ??
                candidate?.Output ??
                candidate?.data ??
                candidate?.Data;
            if (finalUiCommand || previewPayload != null) {
                return candidate;
            }
        }

        if (parsedValue && typeof parsedValue === "object") {
            return parsedValue;
        }

        return parsedItemOutput && typeof parsedItemOutput === "object" ? parsedItemOutput : null;
    };

    const resolveDebugUiCommand = (chatItem) => {
        const direct = String(resolveItemUiCommand(chatItem) ?? "").trim();
        if (direct && direct.toLowerCase() !== "none") {
            return direct;
        }

        const parsedOutput = parseMaybeJson(chatItem?.Output ?? chatItem?.output ?? null);
        if (parsedOutput && typeof parsedOutput === "object") {
            const outputCommand = String(parsedOutput?.uiCommand ?? parsedOutput?.UICommand ?? "").trim();
            if (outputCommand) {
                return outputCommand;
            }
        }

        return direct || "none";
    };

    const buildDebugEntryFromChatItem = (chatItem) => {
        if (!chatItem || typeof chatItem !== "object") {
            return null;
        }

        const chatItemId = getHistoryItemId(chatItem);
        if (!chatItemId) {
            return null;
        }

        return {
            id: `chat:${chatItemId}`,
            uiCommand: resolveDebugUiCommand(chatItem),
            item: chatItem,
            value: parseMaybeJson(chatItem?.Output ?? chatItem?.output ?? null),
            receivedAt:
                chatItem?.UpdatedDate ??
                chatItem?.updatedDate ??
                chatItem?.CreatedDate ??
                chatItem?.createdDate ??
                null
        };
    };

    const getHistoryItemId = (chatItem) => {
        const rawId = chatItem?._id ?? chatItem?.UniqueId ?? null;
        return rawId ? String(rawId) : null;
    };

    const getHistoryItemOutputPayload = (chatItem, fallbackValue = "") => {
        const output = chatItem?.Output ?? chatItem?.output ?? null;
        if (typeof output === "string" && output.trim().length > 0) {
            return output;
        }
        if (output && typeof output === "object") {
            return output;
        }

        return chatItem?.ModelResponse ??
            chatItem?.modelResponse ??
            chatItem?.ResponseMessage ??
            chatItem?.responseMessage ??
            fallbackValue ??
            "";
    };

    const preserveChatMessageFields = (previousItem, nextItem) => {
        if (!previousItem || !nextItem || typeof nextItem !== "object") {
            return nextItem;
        }

        const merged = { ...nextItem };
        const copyTextField = (pascalKey, camelKey) => {
            const value = previousItem?.[pascalKey] ?? previousItem?.[camelKey] ?? null;
            if (typeof value !== "string" || value.trim().length === 0) {
                return;
            }

            if (Object.prototype.hasOwnProperty.call(nextItem, camelKey) &&
                !Object.prototype.hasOwnProperty.call(nextItem, pascalKey)) {
                merged[camelKey] = value;
                return;
            }

            merged[pascalKey] = value;
        };

        copyTextField("Input", "input");
        copyTextField("ModelResponse", "modelResponse");
        copyTextField("ResponseMessage", "responseMessage");
        if (Array.isArray(previousItem?.ChatMessages) && previousItem.ChatMessages.length > 0) {
            merged.ChatMessages = previousItem.ChatMessages;
        } else if (Array.isArray(previousItem?.chatMessages) && previousItem.chatMessages.length > 0) {
            merged.chatMessages = previousItem.chatMessages;
        }
        return merged;
    };

    const getRefreshPayload = (chatItem) => {
        if (!chatItem || typeof chatItem !== "object") {
            return null;
        }

        if (isFailedChatItem(chatItem)) {
            return null;
        }

        const mode = resolveHistoryDisplayMode(chatItem);
        if (!["render", "data", "chart"].includes(mode)) {
            return null;
        }

        const outputKind = String(chatItem?.OutputKind ?? chatItem?.outputKind ?? "").trim().toLowerCase();
        if (outputKind === "preview" || outputKind === "plan") {
            return null;
        }

        const reviewState = String(chatItem?.ReviewState ?? chatItem?.reviewState ?? "").trim().toLowerCase();
        if (reviewState === "pending" || reviewState === "rejected") {
            return null;
        }

        const payload = parseMaybeJson(chatItem?.Output ?? chatItem?.output ?? null);
        if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
            return null;
        }

        const refreshEnabled = payload?.refreshEnabled ?? payload?.RefreshEnabled;
        const isEnabled =
            refreshEnabled === true ||
            String(refreshEnabled ?? "").trim().toLowerCase() === "true";
        const refreshContext = payload?.refreshContext ?? payload?.RefreshContext;
        const datasets = payload?.datasets ?? payload?.Datasets;
        if (!isEnabled || !refreshContext || !Array.isArray(datasets) || datasets.length === 0) {
            return null;
        }

        return payload;
    };

    const buildHistoryFromChats = (items) => {
        const chats = Array.isArray(items) ? items : [];
        const sorted = chats.slice().sort((a, b) => {
            const aTime = getHistorySortTime(a);
            const bTime = getHistorySortTime(b);
            if (bTime !== aTime) return bTime - aTime;
            return (b?.Sequence ?? 0) - (a?.Sequence ?? 0);
        });
        const nextByMode = { render: [], data: [], chart: [] };
        let newestAction = null;
        for (const chatItem of sorted) {
            if (isFailedChatItem(chatItem)) continue;
            const mode = resolveHistoryDisplayMode(chatItem);
            if (!nextByMode[mode]) continue;
            nextByMode[mode].push(chatItem);
            if (!newestAction) {
                newestAction = chatItem;
            }
        }
        return { nextByMode, newestAction };
    };

    const mergeDistinctChatItems = (...collections) => {
        const merged = [];
        const seenIds = new Set();

        collections.forEach((collection) => {
            if (!Array.isArray(collection)) return;
            collection.forEach((chatItem) => {
                if (!chatItem || typeof chatItem !== "object") return;
                const chatItemId = getHistoryItemId(chatItem);
                if (chatItemId) {
                    if (seenIds.has(chatItemId)) return;
                    seenIds.add(chatItemId);
                }
                merged.push(chatItem);
            });
        });

        return merged;
    };

    const mergeDistinctDebugEntries = (...collections) => {
        const merged = [];
        const seenIds = new Set();

        collections.forEach((collection) => {
            if (!Array.isArray(collection)) return;
            collection.forEach((entry) => {
                if (!entry || typeof entry !== "object" || !entry.id) return;
                if (seenIds.has(entry.id)) return;
                seenIds.add(entry.id);
                merged.push(entry);
            });
        });

        return merged;
    };

    const normalizeRows = (payload) => {
        if (!payload || typeof payload !== "object") {
            return [];
        }
        if (Array.isArray(payload?.Rows)) return payload.Rows;
        if (Array.isArray(payload?.rows)) return payload.rows;
        return [];
    };

    const normalizeMetadata = (payload) => {
        if (!payload || typeof payload !== "object") {
            return [];
        }

        const raw = payload?.Metadata ?? payload?.metadata ?? null;
        if (!raw) {
            return [];
        }

        // Already in expected list shape.
        if (Array.isArray(raw)) {
            return raw;
        }

        // Common "single metadata object" shape.
        if (typeof raw === "object" && !Array.isArray(raw)) {
            if (Array.isArray(raw?.Fields) || Array.isArray(raw?.fields)) {
                return [raw];
            }

            // Nested wrappers occasionally returned by backend payloads.
            if (Array.isArray(raw?.Items)) return raw.Items;
            if (Array.isArray(raw?.items)) return raw.items;
            if (Array.isArray(raw?.Metadata)) return raw.Metadata;
            if (Array.isArray(raw?.metadata)) return raw.metadata;
        }

        return [];
    };

    const refreshSessionHistory = async (sessionId, forceReload = false) => {
        if (!sessionId) {
            setHistoryItemsByMode({ render: [], data: [], chart: [] });
            setLatestSessionActionItem(null);
            setDebugSessionEntries([]);
            historyLoadStateRef.current.lastLoadedSessionId = null;
            historyLoadStateRef.current.inFlightSessionId = null;
            return;
        }
        const normalizedSessionId = String(sessionId);
        if (!forceReload && historyLoadStateRef.current.lastLoadedSessionId === normalizedSessionId) {
            return;
        }
        if (historyLoadStateRef.current.inFlightSessionId === normalizedSessionId) {
            return;
        }
        historyLoadStateRef.current.inFlightSessionId = normalizedSessionId;

        let response = null;
        try {
            response = await aiChatMgr.Query({
                model: "x => x.IsActive == true && x.IsDeleted == false && x.ChatSessionId == sessionId",
                params: { sessionId: normalizedSessionId }
            });
        } catch {
            historyLoadStateRef.current.inFlightSessionId = null;
            return;
        }

        if (!response?.Success) {
            setHistoryItemsByMode({ render: [], data: [], chart: [] });
            setLatestSessionActionItem(null);
            setDebugSessionEntries([]);
            historyLoadStateRef.current.inFlightSessionId = null;
            return;
        }

        const chatItems = response?.Items ?? [];
        const { nextByMode, newestAction } = buildHistoryFromChats(chatItems);

        setHistoryItemsByMode(nextByMode);
        setLatestSessionActionItem(newestAction);
        setDebugSessionEntries(
            chatItems
                .map((chatItem) => buildDebugEntryFromChatItem(chatItem))
                .filter(Boolean)
                .sort((a, b) => {
                    const aTime = new Date(a?.receivedAt ?? 0).getTime();
                    const bTime = new Date(b?.receivedAt ?? 0).getTime();
                    if (bTime !== aTime) return bTime - aTime;
                    return String(a?.id ?? "").localeCompare(String(b?.id ?? ""));
                })
        );
        historyLoadStateRef.current.lastLoadedSessionId = normalizedSessionId;
        historyLoadStateRef.current.inFlightSessionId = null;
    };

    const upsertHistoryItem = (chatItem, modeOverride = null) => {
        if (!chatItem || typeof chatItem !== "object") {
            return;
        }
        if (isFailedChatItem(chatItem)) {
            return;
        }
        const mode = modeOverride || resolveHistoryDisplayMode(chatItem);
        if (!["render", "data", "chart"].includes(mode)) {
            return;
        }
        const nextId = getHistoryItemId(chatItem);
        if (!nextId) {
            return;
        }

        setHistoryItemsByMode((previous) => {
            const currentModeItems = previous[mode] || [];
            const merged = [chatItem, ...currentModeItems.filter((entry) => getHistoryItemId(entry) !== nextId)];
            merged.sort((a, b) => {
                const aTime = getHistorySortTime(a);
                const bTime = getHistorySortTime(b);
                if (bTime !== aTime) return bTime - aTime;
                return (b?.Sequence ?? 0) - (a?.Sequence ?? 0);
            });
            return {
                ...previous,
                [mode]: merged
            };
        });
    };

    const isObjectRecord = (value) =>
        value && typeof value === "object" && !Array.isArray(value);

    const hasChartCoordinate = (value) =>
        isObjectRecord(value) || Array.isArray(value);

    const hasRenderableChartShape = (option) => {
        if (!isObjectRecord(option)) {
            return false;
        }

        const series = option.series ?? option.Series;
        const dataset = option.dataset ?? option.Dataset;
        const xAxis = option.xAxis ?? option.XAxis;
        const yAxis = option.yAxis ?? option.YAxis;
        const radar = option.radar ?? option.Radar;
        const polar = option.polar ?? option.Polar;
        const geo = option.geo ?? option.Geo;
        const calendar = option.calendar ?? option.Calendar;
        const singleAxis = option.singleAxis ?? option.SingleAxis;

        return (
            hasChartCoordinate(series) ||
            hasChartCoordinate(dataset) ||
            hasChartCoordinate(xAxis) ||
            hasChartCoordinate(yAxis) ||
            hasChartCoordinate(radar) ||
            hasChartCoordinate(polar) ||
            hasChartCoordinate(geo) ||
            hasChartCoordinate(calendar) ||
            hasChartCoordinate(singleAxis)
        );
    };

    const normalizeEChartsOption = (option) => {
        if (!isObjectRecord(option)) {
            return null;
        }

        const normalized = { ...option };
        const rawTitle = normalized.title ?? normalized.Title;
        if (typeof rawTitle === "string") {
            normalized.title = { text: rawTitle };
            delete normalized.Title;
        }

        return normalized;
    };

    const resolveChartOption = (payload) => {
        if (!payload) {
            return null;
        }

        if (typeof payload === "string") {
            const parsed = tryParseJson(payload);
            return resolveChartOption(parsed);
        }

        if (!isObjectRecord(payload)) {
            return null;
        }

        if (isObjectRecord(payload.option)) {
            return resolveChartOption(payload.option);
        }

        if (isObjectRecord(payload.Option)) {
            return resolveChartOption(payload.Option);
        }

        if (isObjectRecord(payload.echartsOption)) {
            return resolveChartOption(payload.echartsOption);
        }

        if (isObjectRecord(payload.EChartsOption)) {
            return resolveChartOption(payload.EChartsOption);
        }

        if (payload.optionJson) {
            const optionFromJson = typeof payload.optionJson === "string"
                ? tryParseJson(payload.optionJson)
                : payload.optionJson;
            if (isObjectRecord(optionFromJson)) {
                return resolveChartOption(optionFromJson);
            }
        }

        if (payload.OptionJson) {
            const optionFromJson = typeof payload.OptionJson === "string"
                ? tryParseJson(payload.OptionJson)
                : payload.OptionJson;
            if (isObjectRecord(optionFromJson)) {
                return resolveChartOption(optionFromJson);
            }
        }

        if (payload.echartsOptionJson) {
            const optionFromJson = typeof payload.echartsOptionJson === "string"
                ? tryParseJson(payload.echartsOptionJson)
                : payload.echartsOptionJson;
            if (isObjectRecord(optionFromJson)) {
                return resolveChartOption(optionFromJson);
            }
        }

        if (payload.EChartsOptionJson) {
            const optionFromJson = typeof payload.EChartsOptionJson === "string"
                ? tryParseJson(payload.EChartsOptionJson)
                : payload.EChartsOptionJson;
            if (isObjectRecord(optionFromJson)) {
                return resolveChartOption(optionFromJson);
            }
        }

        if (hasRenderableChartShape(payload)) {
            return normalizeEChartsOption(payload);
        }

        return null;
    };

    const resolveArtifactCharts = (payload) => {
        const parsed = parseMaybeJson(payload);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return [];
        }

        const charts = [];
        const pushChart = (candidate, fallbackIndex = charts.length) => {
            if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
                return;
            }
            const option = resolveChartOption(
                candidate.option ??
                candidate.Option ??
                candidate.echartsOption ??
                candidate.EChartsOption ??
                candidate
            );
            if (!option) {
                return;
            }
            const optionKey = (() => {
                try {
                    return JSON.stringify(option);
                } catch {
                    return "";
                }
            })();
            const chartIndex = Number.isFinite(Number(candidate.index)) ? Number(candidate.index) : fallbackIndex;
            if (charts.some((entry) => Number(entry.index) === chartIndex && entry.optionKey === optionKey)) {
                return;
            }

            charts.push({
                index: chartIndex,
                title: candidate.title ?? candidate.Title ?? "",
                datasetAliases: candidate.datasetAliases ?? candidate.DatasetAliases ?? [],
                option,
                optionKey,
                fallbackText: candidate.fallbackText ?? candidate.FallbackText ?? candidate.content ?? candidate.Content ?? ""
            });
        };

        const directCharts = parsed.charts ?? parsed.Charts ?? parsed.chartBlocks ?? parsed.ChartBlocks ?? null;
        if (Array.isArray(directCharts)) {
            directCharts.forEach((chart, index) => pushChart(chart, index));
        }

        const components = parsed.components ?? parsed.Components ?? parsed.args?.components ?? parsed.Args?.Components ?? null;
        if (Array.isArray(components)) {
            components.forEach((component, index) => {
                const kind = String(component?.kind ?? component?.Kind ?? "").trim().toLowerCase();
                if (kind !== "chart_block") {
                    return;
                }
                const properties = component?.properties ?? component?.Properties ?? {};
                pushChart({
                    index,
                    title: component?.title ?? component?.Title,
                    datasetAliases: component?.datasetAliases ?? component?.DatasetAliases,
                    fallbackText: component?.content ?? component?.Content,
                    option: properties?.echartsOption ?? properties?.EChartsOption ?? properties?.option ?? properties?.Option,
                    optionJson: properties?.echartsOptionJson ?? properties?.optionJson
                }, index);
            });
        }

        return charts.sort((a, b) => a.index - b.index);
    };

    const resolveRenderPayload = (value, item = null) => {
        const parsedValue = parseMaybeJson(value);
        const parsedItemOutput = parseMaybeJson(item?.Output ?? item?.output ?? null);
        const candidates = [parsedValue, parsedItemOutput, value, item].filter(Boolean);
        let html = "";
        let charts = [];

        for (const candidate of candidates) {
            if (!html) {
                html = resolveRenderHtml(candidate, item);
            }
            if (charts.length === 0) {
                charts = resolveArtifactCharts(candidate);
            }
            if (html && charts.length > 0) {
                break;
            }
        }

        return { html, charts };
    };

    const applyHistoryItem = (mode, historyItem, overrideValue = undefined, overrideCommand = null) => {
        if (!historyItem) return;
        if (isFailedChatItem(historyItem)) return;
        const payload =
            overrideValue !== undefined
                ? overrideValue
                : getHistoryItemOutputPayload(historyItem);
        const command = overrideCommand ?? resolveItemUiCommand(historyItem, mode);
        onUpdatePanel(command, historyItem, payload);
    };

    const getLatestResultHistoryEntry = () => {
        const entries = [
            ...(historyItemsByMode.render || []).map((item) => ({ mode: "render", item })),
            ...(historyItemsByMode.chart || []).map((item) => ({ mode: "chart", item }))
        ];
        entries.sort((a, b) => {
            const aTime = getHistorySortTime(a.item);
            const bTime = getHistorySortTime(b.item);
            if (bTime !== aTime) return bTime - aTime;
            return (b.item?.Sequence ?? 0) - (a.item?.Sequence ?? 0);
        });
        return entries[0] ?? null;
    };

    const getOutputTabKeyForMode = (mode) => (mode === "data" ? "data" : "result");

    useEffect(() => {
        if (!activeChatSessionId) return;
        refreshSessionHistory(activeChatSessionId);
    }, [activeChatSessionId]);

    useEffect(() => {
        if (!activeChatSessionId) return;
        if (autoLoadedSessionId === activeChatSessionId) return;
        const loadState = historyLoadStateRef.current;
        // Wait until the session history request has completed for this session.
        if (loadState.inFlightSessionId === activeChatSessionId) return;
        if (loadState.lastLoadedSessionId !== activeChatSessionId) return;

        if (!latestSessionActionItem) {
            // Fallback: attempt loading from first available mode bucket if newest action wasn't set.
            const resolvedFallbackEntry =
                (historyItemsByMode.data || []).map((item) => ({ mode: "data", item }))[0] ??
                (historyItemsByMode.render || []).map((item) => ({ mode: "render", item }))[0] ??
                (historyItemsByMode.chart || []).map((item) => ({ mode: "chart", item }))[0] ??
                null;
            if (!resolvedFallbackEntry?.item) {
                setDisplayMode("chat");
                setData([]);
                setUiMetadata([]);
                setRenderHtml("");
                setChartPayload(null);
                setArtifactCharts([]);
                setTabHistorySelection({ result: "", data: "" });
                setAutoLoadedSessionId(activeChatSessionId);
                return;
            }
            const fallbackMode = resolvedFallbackEntry.mode;
            const fallbackItem = resolvedFallbackEntry.item;
            const fallbackId = getHistoryItemId(fallbackItem);
            setDisplayMode(fallbackMode);
            if (fallbackId) {
                const fallbackTabKey = getOutputTabKeyForMode(fallbackMode);
                setTabHistorySelection((previous) => ({ ...previous, [fallbackTabKey]: `history:${fallbackMode}:${fallbackId}` }));
            }
            applyHistoryItem(fallbackMode, fallbackItem);
            setAutoLoadedSessionId(activeChatSessionId);
            return;
        }

        const mode = resolveHistoryDisplayMode(latestSessionActionItem);
        if (!["render", "data", "chart"].includes(mode)) {
            setAutoLoadedSessionId(activeChatSessionId);
            return;
        }

        const latestId = getHistoryItemId(latestSessionActionItem);
        setDisplayMode(mode);
        if (latestId) {
            const latestTabKey = getOutputTabKeyForMode(mode);
            setTabHistorySelection((previous) => ({ ...previous, [latestTabKey]: `history:${mode}:${latestId}` }));
        }
        applyHistoryItem(mode, latestSessionActionItem);
        setAutoLoadedSessionId(activeChatSessionId);
    }, [activeChatSessionId, activeChatSessionItem, autoLoadedSessionId, latestSessionActionItem, historyItemsByMode]);

    useEffect(() => {
        setTabHistorySelection((previous) => {
            const next = { ...previous };
            for (const mode of ["result", "data"]) {
                const modeOptions = tabHistoryOptions[mode] || [];
                if (!modeOptions.length) continue;
                const hasSelected = modeOptions.some((option) => option.value === previous[mode]);
                if (!hasSelected) {
                    next[mode] = modeOptions[0].value;
                }
            }
            return next;
        });
    }, [tabHistoryOptions]);

    const switchTab = (mode) => {
        if (mode === "result") {
            if ((displayMode === "render" && hasRenderContent) || (displayMode === "chart" && hasChartContent)) {
                return;
            }
            const latestEntry = getLatestResultHistoryEntry();
            if (latestEntry?.item) {
                const latestId = getHistoryItemId(latestEntry.item);
                if (latestId) {
                    setTabHistorySelection((previous) => ({ ...previous, result: `history:${latestEntry.mode}:${latestId}` }));
                }
                applyHistoryItem(latestEntry.mode, latestEntry.item);
            } else {
                setDisplayMode("render");
            }
            return;
        }

        if (mode === "data") {
            setDisplayMode("data");
            if (!hasRows) {
                const latest = historyItemsByMode.data?.[0];
                if (latest) {
                    const latestId = getHistoryItemId(latest);
                    setTabHistorySelection((previous) => ({ ...previous, data: `history:data:${latestId}` }));
                    applyHistoryItem("data", latest);
                }
            }
            return;
        }
    };

    const handleHistorySelection = (mode, selectedValue) => {
        setTabHistorySelection((previous) => ({ ...previous, [mode]: selectedValue }));
        if (selectedValue.startsWith("history:")) {
            const [, selectedMode, selectedId] = selectedValue.split(":");
            if (!selectedMode || !selectedId) return;
            const selectedItem = (historyItemsByMode[selectedMode] || []).find((entry) => {
                const id = entry?._id ?? entry?.UniqueId;
                return String(id) === selectedId;
            });
            if (!selectedItem) return;
            applyHistoryItem(selectedMode, selectedItem);
        }
    };

    const handleOpenAssociatedOutput = (chatItem, target = null) => {
        if (!chatItem || typeof chatItem !== "object") return;

        const mode = normalizeUiCommand(target?.panelCommand ?? target?.kind ?? resolveItemUiCommand(chatItem));
        if (!["render", "data", "chart"].includes(mode)) return;

        upsertHistoryItem(chatItem, mode);

        const chatItemId = getHistoryItemId(chatItem);
        const outputTabKey = getOutputTabKeyForMode(mode);
        if (chatItemId) {
            setTabHistorySelection((previous) => ({
                ...previous,
                [outputTabKey]: `history:${mode}:${chatItemId}`
            }));
        }

        applyHistoryItem(mode, chatItem, target?.panelValue, target?.panelCommand ?? mode);

        window.requestAnimationFrame?.(() => {
            outputPaneRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
            outputPaneRef.current?.focus?.({ preventScroll: true });
        });
    };

    const getSelectedHistoryEntry = (mode) => {
        const selectedValue = tabHistorySelection[mode] || "";
        if (!selectedValue.startsWith("history:")) {
            return null;
        }
        const [, selectedMode, selectedId] = selectedValue.split(":");
        if (!selectedMode || !selectedId) {
            return null;
        }
        const item = (historyItemsByMode[selectedMode] || []).find((entry) => {
            const id = entry?._id ?? entry?.UniqueId;
            return String(id) === selectedId;
        }) || null;

        return item ? { mode: selectedMode, item } : null;
    };

    const getSelectedHistoryItem = (mode) => {
        return getSelectedHistoryEntry(mode)?.item ?? null;
    };

    const getSelectedHistoryHelpText = (mode) => {
        const selectedItem = getSelectedHistoryItem(mode);
        if (!selectedItem) {
            return "Select a dated result to preview its message.";
        }
        const text = getChatItemMessage(selectedItem);
        return text || "No message available for the selected item.";
    };

    const selectedRefreshHistoryEntry = getSelectedHistoryEntry(activeOutputTabKey);
    const selectedRefreshHistoryItem = selectedRefreshHistoryEntry?.item ?? null;
    const selectedRefreshPayload = getRefreshPayload(selectedRefreshHistoryItem);
    const canRefreshSelectedHistoryItem = !!selectedRefreshPayload;

    useEffect(() => {
        setRefreshError("");
    }, [tabHistorySelection.result, tabHistorySelection.data]);

    const formatRefreshFailure = (response, fallback = "Refresh failed.") => {
        const details = response?.MessageDetails ?? response?.messageDetails;
        if (Array.isArray(details)) {
            const text = details.map((entry) => String(entry ?? "").trim()).filter(Boolean).join(" ");
            if (text) return text;
        }

        const message =
            response?.DisplayMessage ??
            response?.displayMessage ??
            response?.Message?.Info ??
            response?.message?.info ??
            response?.Message?.Content ??
            response?.message?.content ??
            response?.Exception?.Message ??
            response?.exception?.message ??
            "";

        return String(message || fallback).trim();
    };

    const handleRefreshSelectedResult = async () => {
        if (!canRefreshSelectedHistoryItem || refreshInFlight) {
            return;
        }

        const sourceItem = selectedRefreshHistoryItem;
        const resultChatId = getHistoryItemId(sourceItem);
        if (!resultChatId) {
            setRefreshError("Refresh requires a saved result.");
            return;
        }

        if (typeof aiChatMgr?.RunOperation !== "function") {
            setRefreshError("Refresh operation is not available.");
            return;
        }

        setRefreshError("");
        setRefreshInFlight(true);
        try {
            const response = await aiChatMgr.RunOperation({
                operationName: "AIRefreshResult",
                item: { resultChatId },
                isItem: false
            });

            if (!response?.Success) {
                setRefreshError(formatRefreshFailure(response));
                return;
            }

            const returnedItem =
                (Array.isArray(response?.Items) ? response.Items[0] : null) ??
                response?.Item ??
                response?.item ??
                null;
            const refreshedItem = preserveChatMessageFields(sourceItem, returnedItem);
            if (!refreshedItem) {
                setRefreshError("Refresh completed without a result item.");
                return;
            }

            const uiCommands = response?.UICommands ?? response?.uiCommands ?? {};
            const uiCommand =
                uiCommands?.AIAction ??
                uiCommands?.aiAction ??
                refreshedItem?.UICommand ??
                refreshedItem?.uiCommand ??
                resolveItemUiCommand(refreshedItem, selectedRefreshHistoryEntry?.mode);
            const normalizedMode = normalizeUiCommand(uiCommand);
            const refreshedMode = ["render", "data", "chart"].includes(normalizedMode)
                ? normalizedMode
                : selectedRefreshHistoryEntry?.mode ?? resolveHistoryDisplayMode(refreshedItem);
            const refreshedValue =
                response?.Value !== undefined
                    ? response.Value
                    : response?.value !== undefined
                        ? response.value
                        : parseMaybeJson(refreshedItem?.Output ?? refreshedItem?.output ?? null);
            const refreshedId = getHistoryItemId(refreshedItem);
            const outputTabKey = getOutputTabKeyForMode(refreshedMode);

            upsertHistoryItem(refreshedItem, refreshedMode);
            setLatestSessionActionItem(refreshedItem);
            if (refreshedId) {
                setTabHistorySelection((previous) => ({
                    ...previous,
                    [outputTabKey]: `history:${refreshedMode}:${refreshedId}`
                }));
            }
            await onUpdatePanel(
                uiCommand,
                refreshedItem,
                getHistoryItemOutputPayload(refreshedItem, refreshedValue)
            );

            const sessionId = refreshedItem?.ChatSessionId ?? refreshedItem?.chatSessionId ?? activeChatSessionId;
            if (sessionId) {
                void refreshSessionHistory(String(sessionId), true);
            }
        } catch (error) {
            setRefreshError(String(error?.message ?? error ?? "Refresh failed."));
        } finally {
            setRefreshInFlight(false);
        }
    };

    const handleSessionChange = (sessionId, _sessionItem = null, sessionChats = null) => {
        const updateSessionSearchParam = (nextSessionId) => {
            const currentSessionId = String(searchParams.get(sidParamName) ?? "").trim();
            const normalizedNextSessionId = String(nextSessionId ?? "").trim();
            if (currentSessionId === normalizedNextSessionId) {
                return;
            }

            const nextParams = new URLSearchParams(searchParams);
            if (normalizedNextSessionId) {
                nextParams.set(sidParamName, normalizedNextSessionId);
            } else {
                nextParams.delete(sidParamName);
            }
            setSearchParams(nextParams);
        };

        if (!sessionId) {
            updateSessionSearchParam(null);
            setActiveChatSessionId(null);
            setActiveChatSessionItem(null);
            setHistoryItemsByMode({ render: [], data: [], chart: [] });
            setLatestSessionActionItem(null);
            setDebugSessionEntries([]);
            setAutoLoadedSessionId(null);
            historyLoadStateRef.current.lastLoadedSessionId = null;
            historyLoadStateRef.current.inFlightSessionId = null;
            setTabHistorySelection({ result: "", data: "" });
            setDisplayMode("chat");
            setData([]);
            setUiMetadata([]);
            setRenderHtml("");
            setChartPayload(null);
            setArtifactCharts([]);
            setHasAskedQuestion(false);
            setDebugPayload({
                uiCommand: null,
                item: null,
                value: null,
                receivedAt: null
            });
            return;
        }
        const nextSessionId = String(sessionId);
        updateSessionSearchParam(nextSessionId);
        setActiveChatSessionItem(_sessionItem ?? null);
        if (Array.isArray(sessionChats)) {
            const shouldMergeSessionChats =
                nextSessionId === activeChatSessionId &&
                sessionChats.length > 0;
            const mergedSessionChats = shouldMergeSessionChats
                ? mergeDistinctChatItems(
                    sessionChats,
                    historyItemsByMode.render,
                    historyItemsByMode.data,
                    historyItemsByMode.chart
                )
                : sessionChats;
            const { nextByMode, newestAction } = buildHistoryFromChats(mergedSessionChats, _sessionItem);
            setHistoryItemsByMode(nextByMode);
            setLatestSessionActionItem(newestAction);
            setAutoLoadedSessionId(null);
            const nextDebugEntries = mergedSessionChats
                .map((chatItem) => buildDebugEntryFromChatItem(chatItem))
                .filter(Boolean)
                .sort((a, b) => {
                    const aTime = new Date(a?.receivedAt ?? 0).getTime();
                    const bTime = new Date(b?.receivedAt ?? 0).getTime();
                    if (bTime !== aTime) return bTime - aTime;
                    return String(a?.id ?? "").localeCompare(String(b?.id ?? ""));
                });
            setDebugSessionEntries(
                shouldMergeSessionChats
                    ? mergeDistinctDebugEntries(nextDebugEntries, debugSessionEntries).sort((a, b) => {
                        const aTime = new Date(a?.receivedAt ?? 0).getTime();
                        const bTime = new Date(b?.receivedAt ?? 0).getTime();
                        if (bTime !== aTime) return bTime - aTime;
                        return String(a?.id ?? "").localeCompare(String(b?.id ?? ""));
                    })
                    : nextDebugEntries
            );
            historyLoadStateRef.current.lastLoadedSessionId = nextSessionId;
            historyLoadStateRef.current.inFlightSessionId = null;
        }
        if (nextSessionId !== activeChatSessionId) {
            setAutoLoadedSessionId(null);
            if (!Array.isArray(sessionChats)) {
                historyLoadStateRef.current.lastLoadedSessionId = null;
            }
        }
        setActiveChatSessionId(nextSessionId);
    };

    const buildExportFileName = (extension) => {
        const stamp = new Date().toISOString().replace(/[:.]/g, "-");
        return `warehouse-output-${stamp}.${extension}`;
    };

    const downloadDataUrl = (dataUrl, fileName) => {
        if (!dataUrl) {
            return;
        }
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportNodeToPng = async (node) => {
        if (!node) {
            return null;
        }
        const rect = node.getBoundingClientRect();
        const width = Math.max(1, Math.round(node.scrollWidth || rect.width));
        const height = Math.max(1, Math.round(node.scrollHeight || rect.height));
        const baseScale = Math.min(2, window.devicePixelRatio || 1);
        const maxCanvasDimension = 8192;
        const dimensionScale = Math.min(1, maxCanvasDimension / width, maxCanvasDimension / height);
        const scale = Math.max(0.25, baseScale * dimensionScale);
        const exportMarker = "data-export-root";
        const originalMarker = node.getAttribute(exportMarker);
        node.setAttribute(exportMarker, "true");

        let canvas = null;
        try {
            canvas = await html2canvas(node, {
                backgroundColor: "#ffffff",
                useCORS: true,
                allowTaint: false,
                logging: false,
                scale,
                width,
                height,
                windowWidth: Math.max(document.documentElement.clientWidth, width),
                windowHeight: Math.max(document.documentElement.clientHeight, height),
                scrollX: 0,
                scrollY: 0,
                onclone: (clonedDocument) => {
                    const clonedNode = clonedDocument.querySelector(`[${exportMarker}="true"]`);
                    if (clonedNode) {
                        clonedNode.style.background = "#ffffff";
                        clonedNode.style.backgroundColor = "#ffffff";
                        clonedNode.style.boxShadow = "none";
                        clonedNode.style.filter = "none";
                        clonedNode.style.overflow = "visible";
                        clonedNode.style.maxHeight = "none";
                        clonedNode.style.height = "auto";
                        clonedNode.style.maxWidth = "none";
                    }

                    // Remove scrollbars from nested containers so export captures full content.
                    if (clonedNode) {
                        const descendants = clonedNode.querySelectorAll("*");
                        descendants.forEach((el) => {
                            if (!(el instanceof HTMLElement)) return;
                            const hasVerticalScroll = el.scrollHeight > el.clientHeight;
                            const hasHorizontalScroll = el.scrollWidth > el.clientWidth;
                            if (hasVerticalScroll || hasHorizontalScroll) {
                                el.style.overflow = "visible";
                                el.style.overflowX = "visible";
                                el.style.overflowY = "visible";
                                el.style.maxHeight = "none";
                                el.style.height = "auto";
                                el.style.maxWidth = "none";
                            }
                        });
                    }
                }
            });
        } finally {
            if (originalMarker === null) {
                node.removeAttribute(exportMarker);
            } else {
                node.setAttribute(exportMarker, originalMarker);
            }
        }

        return canvas.toDataURL("image/png");
    };

    const mountTemporaryRenderExportSurface = () => {
        const html = String(sanitizedRenderHtml || "").trim();
        if (!html) {
            return null;
        }

        const host = document.createElement("div");
        host.className = "search-warehouse-render";
        host.style.position = "fixed";
        host.style.left = "0";
        host.style.top = "0";
        host.style.width = `${Math.max(720, outputPaneRef.current?.clientWidth || 1000)}px`;
        host.style.height = "auto";
        host.style.overflow = "visible";
        host.style.pointerEvents = "none";
        host.style.opacity = "0";
        host.style.zIndex = "0";
        host.style.background = "#ffffff";

        const surface = document.createElement("div");
        surface.className = "search-warehouse-render__surface";
        surface.innerHTML = html;
        host.appendChild(surface);
        document.body.appendChild(host);

        return {
            host,
            surface,
            cleanup: () => {
                if (host.parentNode) {
                    host.parentNode.removeChild(host);
                }
            }
        };
    };

    const copyCanvasSnapshotsToClone = (sourceNode, clonedNode) => {
        if (!sourceNode || !clonedNode) {
            return;
        }

        const sourceCanvases = Array.from(sourceNode.querySelectorAll("canvas"));
        const clonedCanvases = Array.from(clonedNode.querySelectorAll("canvas"));
        sourceCanvases.forEach((canvas, index) => {
            const clonedCanvas = clonedCanvases[index];
            if (!clonedCanvas?.parentNode || typeof canvas?.toDataURL !== "function") {
                return;
            }

            try {
                const img = document.createElement("img");
                img.src = canvas.toDataURL("image/png");
                img.alt = "chart export";
                img.className = "search-warehouse-print-chart-image";
                img.style.display = "block";
                img.style.width = "100%";
                img.style.maxWidth = "100%";
                img.style.height = "auto";
                clonedCanvas.parentNode.replaceChild(img, clonedCanvas);
            } catch {
                // Browser security or canvas state can block export; leave the cloned canvas in place.
            }
        });
    };

    const mountPrintableOutputClone = (sourceNode) => {
        if (!sourceNode) {
            return null;
        }

        const printContent = document.createElement("div");
        printContent.className = "search-warehouse-print-content";
        const clonedNode = sourceNode.cloneNode(true);
        copyCanvasSnapshotsToClone(sourceNode, clonedNode);
        printContent.appendChild(clonedNode);
        document.body.appendChild(printContent);

        return {
            printContent,
            cleanup: () => {
                if (printContent.parentNode) {
                    printContent.parentNode.removeChild(printContent);
                }
            }
        };
    };

    const handleExportPdf = () => {
        if (displayMode === "chart" && chartRef.current?.getEchartsInstance) {
            const chart = chartRef.current.getEchartsInstance();
            const dataUrl = chart?.getDataURL?.({
                type: "png",
                pixelRatio: 2,
                backgroundColor: "#ffffff"
            });
            if (!dataUrl) {
                return;
            }
            const body = document.body;
            const printImage = document.createElement("div");
            printImage.className = "search-warehouse-print-image";
            printImage.innerHTML = `<img src="${dataUrl}" alt="chart export" />`;
            body.appendChild(printImage);
            body.classList.add("search-warehouse-printing-image");

            const cleanup = () => {
                body.classList.remove("search-warehouse-printing-image");
                if (printImage.parentNode) {
                    printImage.parentNode.removeChild(printImage);
                }
            };

            window.addEventListener("afterprint", cleanup, { once: true });
            setTimeout(cleanup, 15000);

            // Let DOM paint before printing.
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    window.print();
                });
            });
            return;
        }

        if (displayMode === "render" && hasArtifactCharts) {
            const mounted = mountPrintableOutputClone(outputPaneRef.current);
            if (!mounted?.printContent) return;

            const body = document.body;
            body.classList.add("search-warehouse-printing-content");

            const cleanup = () => {
                body.classList.remove("search-warehouse-printing-content");
                mounted.cleanup();
            };

            window.addEventListener("afterprint", cleanup, { once: true });
            setTimeout(cleanup, 15000);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => window.print());
            });
            return;
        }

        if (displayMode === "render" && !hasArtifactCharts) {
            const mounted = mountTemporaryRenderExportSurface();
            if (!mounted?.surface) return;

            const body = document.body;
            const printContent = document.createElement("div");
            printContent.className = "search-warehouse-print-content";
            printContent.appendChild(mounted.surface.cloneNode(true));
            body.appendChild(printContent);
            body.classList.add("search-warehouse-printing-content");

            const cleanup = () => {
                body.classList.remove("search-warehouse-printing-content");
                if (printContent.parentNode) {
                    printContent.parentNode.removeChild(printContent);
                }
                mounted.cleanup();
            };

            window.addEventListener("afterprint", cleanup, { once: true });
            setTimeout(cleanup, 15000);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => window.print());
            });
            return;
        }

        const node = outputPaneRef.current;
        if (!node) {
            return;
        }

        const body = document.body;
        body.classList.add("search-warehouse-printing");

        const cleanup = () => {
            body.classList.remove("search-warehouse-printing");
        };

        window.addEventListener("afterprint", cleanup, { once: true });

        // Fallback cleanup in case afterprint is not emitted by the browser.
        setTimeout(cleanup, 1500);

        window.print();
    };

    const handleExportImage = async () => {
        const fileName = buildExportFileName("png");
        if (displayMode === "chart" && chartRef.current?.getEchartsInstance) {
            const chart = chartRef.current.getEchartsInstance();
            const dataUrl = chart?.getDataURL?.({
                type: "png",
                pixelRatio: 2,
                backgroundColor: "#ffffff"
            });
            downloadDataUrl(dataUrl, fileName);
            return;
        }

        let dataUrl = null;
        if (displayMode === "render" && !hasArtifactCharts) {
            const mounted = mountTemporaryRenderExportSurface();
            if (!mounted?.surface) return;
            try {
                dataUrl = await exportNodeToPng(mounted.surface);
            } finally {
                mounted.cleanup();
            }
        } else {
            const node = outputPaneRef.current;
            if (!node) return;
            dataUrl = await exportNodeToPng(node);
        }
        downloadDataUrl(dataUrl, fileName);
    };

    const handleExportExcel = () => {
        // TODO: implement Excel export for current table data payload.
    };

    const handleExportCsv = () => {
        // TODO: implement CSV export for current table data payload.
    };

    const formatDebugValue = (input) => {
        if (input === undefined) {
            return "undefined";
        }
        if (input === null) {
            return "empty";
        }
        if (typeof input === "string") {
            return input;
        }
        try {
            return JSON.stringify(input, null, 2);
        } catch (error) {
            return `Unable to serialize value: ${error?.message ?? "unknown error"}`;
        }
    };

    const copyDebugValue = async (key, input) => {
        const text = formatDebugValue(input);
        try {
            await navigator.clipboard.writeText(text);
            setLastCopiedKey(key);
            setTimeout(() => setLastCopiedKey((prev) => (prev === key ? null : prev)), 1200);
        } catch {
            // no-op; clipboard may be blocked by browser permissions
        }
    };

    const persistDebugHistory = (entries) => {
        try {
            window.sessionStorage.setItem(debugHistoryStorageKey, JSON.stringify(entries));
        } catch {
            // Ignore debug-history storage failures.
        }
    };

    const formatDebugHistoryLabel = (entry) => {
        const commandLabel = String(entry?.uiCommand ?? "none").trim() || "none";
        const timestamp = String(entry?.receivedAt ?? "").trim();
        if (!timestamp) {
            return commandLabel;
        }

        const parsed = new Date(timestamp);
        const timeLabel = Number.isNaN(parsed.getTime())
            ? timestamp
            : parsed.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" });
        return `${timeLabel} - ${commandLabel}`;
    };

    const clearDebugHistory = () => {
        setDebugHistory([]);
        setDebugSessionEntries([]);
        setSelectedDebugEntryId(null);
        setDebugPayload({
            uiCommand: null,
            item: null,
            value: null,
            receivedAt: null
        });
        try {
            window.sessionStorage.removeItem(debugHistoryStorageKey);
        } catch {
            // Ignore debug-history storage failures.
        }
    };

    const renderArtifactChart = (chart, key) => {
        const chartOption = resolveChartOption(chart?.option ?? chart?.Option ?? chart);
        if (!chartOption) {
            return (
                <div key={key} className="search-warehouse-state search-warehouse-state--inline">
                    <div className="search-warehouse-state__title">Invalid chart payload</div>
                    <div className="search-warehouse-state__subtitle">
                        This artifact chart block did not include a valid ECharts option.
                    </div>
                </div>
            );
        }

        return (
            <div key={key} className="search-warehouse-artifact-chart">
                <ReactECharts
                    className="search-warehouse-artifact-chart__echarts"
                    option={chartOption}
                    notMerge={true}
                    lazyUpdate={true}
                    style={{ width: "100%", height: "100%", minHeight: 0 }}
                />
            </div>
        );
    };

    const renderHtmlSegment = (html, key) => {
        if (!String(html || "").trim()) {
            return null;
        }

        return (
            <div
                key={key}
                className="search-warehouse-render__html-segment"
                dangerouslySetInnerHTML={{__html: html}}
            />
        );
    };

    const renderResultSurfaceContent = () => {
        const html = String(sanitizedRenderHtml || "");
        const charts = Array.isArray(artifactCharts) ? artifactCharts : [];
        if (charts.length === 0) {
            return renderHtmlSegment(html, "html-only");
        }

        const usedChartIndexes = new Set();
        const renderNode = (node, key) => {
            if (node.nodeType === Node.TEXT_NODE) {
                return node.textContent;
            }
            if (node.nodeType !== Node.ELEMENT_NODE) {
                return null;
            }

            const element = node;
            const className = element.getAttribute("class") || "";
            const slotMatch = className.match(/(?:^|\s)ai-artifact-chart-slot-(\d+)(?:\s|$)/);
            if (slotMatch) {
                const chartIndex = Number(slotMatch[1]);
                const chart = charts.find((entry) => Number(entry.index) === chartIndex) ?? charts[chartIndex];
                if (!chart) {
                    return null;
                }
                usedChartIndexes.add(Number(chart.index ?? chartIndex));
                return renderArtifactChart(chart, `chart-${chartIndex}-${key}`);
            }

            const tagName = element.tagName.toLowerCase();
            const props = { key };
            const copyAttr = (attrName, propName = attrName) => {
                const attrValue = element.getAttribute(attrName);
                if (attrValue !== null) {
                    props[propName] = attrValue;
                }
            };

            copyAttr("class", "className");
            copyAttr("title");
            copyAttr("role");
            copyAttr("aria-label");
            copyAttr("aria-hidden");
            copyAttr("href");
            copyAttr("target");
            copyAttr("rel");
            copyAttr("scope");
            copyAttr("colspan", "colSpan");
            copyAttr("rowspan", "rowSpan");

            const children = Array.from(element.childNodes)
                .map((child, index) => renderNode(child, `${key}-${index}`))
                .filter((child) => child !== null && child !== undefined);

            return React.createElement(tagName, props, ...children);
        };

        const template = document.createElement("template");
        template.innerHTML = html;
        const parts = Array.from(template.content.childNodes)
            .map((node, index) => renderNode(node, `node-${index}`))
            .filter((node) => node !== null && node !== undefined);

        charts.forEach((chart, index) => {
            const chartIndex = Number(chart.index ?? index);
            if (!usedChartIndexes.has(chartIndex)) {
                parts.push(renderArtifactChart(chart, `chart-extra-${chartIndex}-${index}`));
            }
        });

        return parts.filter(Boolean);
    };

    // TODO: Fill these in as you wire up unmanaged table behaviors.
    const unmanagedTableOptions = {
        // columns: [], // Optional explicit columns (otherwise derived from metadata.Fields)
        // rowKey: (row) => row?._id ?? row?.id ?? row?.UniqueId,
        // overlayWhenLoading: true,
        // selection: { enabled: true, selectedKeys: [], onChange: (nextKeys) => {} },
        // sorting: { sortBy: '', sortDir: 'asc' },
        // paging: {
        //     enabled: true,
        //     mode: 'paged', // or 'infinite'
        //     page: 1,
        //     pageSize: 50,
        //     totalCount: 0,
        //     pageSizes: [25, 50, 100],
        //     onPageChange: (nextPage) => {},
        //     onPageSizeChange: (nextSize) => {},
        //     infinite: { hasMore: true, loadingMore: false, onLoadMore: () => {}, thresholdPx: 240 }
        // },
        // columnWidths: [{ colName: '', width: 240 }],
        // actions: [{ IconName: '', DisplayName: '', Title: '', Sequence: 0, ActionCallback: (ctx) => {} }],
        // onActionError: (error, action, ctx) => {},
        // onSortChange: ({ sortBy, sortDir, column }) => {},
        // onRowClick: ({ event, row, rowIndex }) => {},
        // onRowDoubleClick: ({ event, row, rowIndex }) => {},
        // onCellClick: ({ event, row, column, value, rowIndex, colIndex }) => {},
        // onCellDoubleClick: ({ event, row, column, value, rowIndex, colIndex }) => {},
        // onScroll: (event) => {},
        // virtualize: true,
        // rowHeight: 44,
        // overscan: 8,
        // disableVirtualizeWhenPaging: true,
        stickyHeader: true,
        // HeaderComponent: CustomHeaderComponent,
        // FooterComponent: CustomFooterComponent,
        // IconComponent: CustomIconComponent,
        // renderContextMenu: ({ row, col, actions, close }) => null,
        // hostSelectorForContextMenu: '.app',
        // emptyContent: <span>No records</span>
    };

    const onUpdatePanel = async (UICommand, item, value)=>{
        const sessionId = resolveSessionId(item, value);
        const nextSessionId = sessionId ? String(sessionId) : null;
        if (nextSessionId && nextSessionId !== activeChatSessionId) {
            setAutoLoadedSessionId(null);
            setActiveChatSessionId(nextSessionId);
        }
        // Keep history dropdown live without waiting for a full AIChat re-query.
        if (item && typeof item === "object") {
            const historyMode = normalizeUiCommand(resolveItemUiCommand(item, UICommand));
            upsertHistoryItem(item, historyMode);
        }

        if (UICommand && !hasAskedQuestion) {
            setHasAskedQuestion(true);
        }
        const nextDebugPayload = {
            uiCommand: UICommand ?? null,
            item: item ?? null,
            value: value ?? null,
            receivedAt: new Date().toISOString()
        };
        setDebugPayload(nextDebugPayload);
        setDebugHistory((previous) => {
            const nextEntry = {
                id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                ...nextDebugPayload
            };
            const nextHistory = [nextEntry, ...previous].slice(0, DEBUG_HISTORY_MAX);
            persistDebugHistory(nextHistory);
            setSelectedDebugEntryId(nextEntry.id);
            return nextHistory;
        });

        if (isFailedChatItem(item)) {
            return;
        }

        let rawCommand = String(UICommand ?? "").trim().toLowerCase();
        let effectiveValue = value;
        if (rawCommand === "preview") {
            const preview = resolvePreviewPayload(value, item);
            rawCommand = String(preview?.finalUiCommand ?? preview?.FinalUiCommand ?? "").trim().toLowerCase();
            effectiveValue =
                preview?.payload ??
                preview?.Payload ??
                preview?.output ??
                preview?.Output ??
                preview?.data ??
                preview?.Data ??
                value;
        }
        const nextMode = rawCommand === "display" ? "data" : rawCommand;
        const supportedModes = new Set(["render", "data", "chart"]);

        if (!supportedModes.has(nextMode)) {
            setDisplayMode("chat");
            setData([]);
            setUiMetadata([]);
            setRenderHtml("");
            setChartPayload(null);
            setArtifactCharts([]);
            return;
        }

        setDisplayMode(nextMode);

        if (nextMode === "render") {
            const { html, charts } = resolveRenderPayload(effectiveValue, item);
            setRenderHtml(html);
            setArtifactCharts(charts);
            setData([]);
            setUiMetadata([]);
            setChartPayload(null);
            return;
        }

        if (nextMode === "data") {
            const payload = parseMaybeJson(effectiveValue);
            const nextRows = normalizeRows(payload);
            const nextMetadata = normalizeMetadata(payload);

            setData(nextRows);
            setUiMetadata(nextMetadata);
            setRenderHtml("");
            setChartPayload(null);
            setArtifactCharts([]);
            return;
        }

        if (nextMode === "chart") {
            const payload = parseMaybeJson(effectiveValue);
            const nextChartPayload = payload && typeof payload === "object" && !Array.isArray(payload)
                ? payload
                : null;
            setChartPayload(nextChartPayload);
            setData([]);
            setUiMetadata([]);
            setRenderHtml("");
            setArtifactCharts([]);
            return;
        }
    }

    const debugDrawerContent = (
        <div className="search-warehouse-debug">
            <div className="search-warehouse-debug__meta">
                <span>Mode: <b>{displayMode}</b></span>
                <span>UICommand: <b>{String(selectedDebugPayload?.uiCommand ?? "none")}</b></span>
                <span>Last update: <b>{selectedDebugPayload?.receivedAt ?? "none yet"}</b></span>
            </div>
            <div className="search-warehouse-debug__layout">
                <div className="search-warehouse-debug__history">
                    <div className="search-warehouse-debug__label">
                        <span>Messages</span>
                        <button
                            type="button"
                            className="search-warehouse-debug__copy"
                            onClick={clearDebugHistory}
                            disabled={effectiveDebugHistory.length === 0}
                        >
                            Clear
                        </button>
                    </div>
                    <div className="search-warehouse-debug__history-list">
                        {effectiveDebugHistory.length > 0 ? effectiveDebugHistory.map((entry) => (
                            <button
                                type="button"
                                key={entry.id}
                                className={`search-warehouse-debug__history-item${selectedDebugEntryId === entry.id ? " search-warehouse-debug__history-item--active" : ""}`}
                                onClick={() => setSelectedDebugEntryId(entry.id)}
                            >
                                {formatDebugHistoryLabel(entry)}
                            </button>
                        )) : (
                            <div className="search-warehouse-debug__history-empty">No debug messages yet.</div>
                        )}
                    </div>
                </div>
                <div className="search-warehouse-debug__grid">
                    <div className="search-warehouse-debug__block">
                        <div className="search-warehouse-debug__label">
                            <span>Item</span>
                            <button
                                type="button"
                                className="search-warehouse-debug__copy"
                                onClick={() => copyDebugValue("item", selectedDebugPayload?.item)}
                            >
                                {lastCopiedKey === "item" ? "Copied" : "Copy"}
                            </button>
                        </div>
                        <pre>{formatDebugValue(selectedDebugPayload?.item)}</pre>
                    </div>
                    <div className="search-warehouse-debug__block">
                        <div className="search-warehouse-debug__label">
                            <span>Value</span>
                            <button
                                type="button"
                                className="search-warehouse-debug__copy"
                                onClick={() => copyDebugValue("value", selectedDebugPayload?.value)}
                            >
                                {lastCopiedKey === "value" ? "Copied" : "Copy"}
                            </button>
                        </div>
                        <pre>{formatDebugValue(selectedDebugPayload?.value)}</pre>
                    </div>
                </div>
            </div>
        </div>
    );

    return ( <>
                    <AIPanel
                        childrenRef={pageRef}
                        open={panelOpen}
                        presentation={panelMode ? "slideout" : "inline"}
                        onUpdatePanel={onUpdatePanel}
                        onSessionChange={handleSessionChange}
                        session={selectedChatSession}
                        onOpenAssociatedOutput={handleOpenAssociatedOutput}
                        mapToClass={'core-layout'}
                        params={{
                            wdId,
                            wId,
                            warehouseDeploymentId: wdId,
                            warehouseId: wId
                        }}
                        referenceId={wdId}
                        referenceName={"WarehouseDeployment"}
                        splashMessage={"Ask me a question about your warehouse!"}
                        operationName = {"AIQuestionWarehouse"}
                        previewPanelEnabled={true}
                        bottomDrawerEnabled={showDebugPayload}
                        bottomDrawerDefaultOpen={false}
                        bottomDrawerTitle={"AI Callback Debug"}
                        bottomDrawerContent={debugDrawerContent}
                            onCancel={()=>{
                                if (typeof onPanelClose === "function") {
                                    onPanelClose();
                                } else {
                                    coreNavigate("/dynamic/warehouse/");
                                }
                            }}
                        >
                    <div className="ai-panel__table search-warehouse-table-pane">
                            <div className="search-warehouse-toolbar">
                                <div className="search-warehouse-toolbar__left">
                                    <div className="search-warehouse-beta-badge">BETA</div>
                                    <div className="search-warehouse-toolbar__actions">
                                        {isDataMode ? (
                                            <>
                                                <button
                                                    type="button"
                                                    className="search-warehouse-toolbar__button"
                                                    onClick={handleExportExcel}
                                                    disabled={!hasRows}
                                                >
                                                    Export Excel
                                                </button>
                                                <button
                                                    type="button"
                                                    className="search-warehouse-toolbar__button"
                                                    onClick={handleExportCsv}
                                                    disabled={!hasRows}
                                                >
                                                    Export CSV
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    className="search-warehouse-toolbar__button"
                                                    onClick={handleExportPdf}
                                                    disabled={!hasDisplayableContent}
                                                >
                                                    Export PDF
                                                </button>
                                                <button
                                                    type="button"
                                                    className="search-warehouse-toolbar__button"
                                                    onClick={handleExportImage}
                                                    disabled={!hasDisplayableContent}
                                                >
                                                    Export Image
                                                </button>
                                            </>
                                        )}
                                        {refreshError && (
                                            <span className="search-warehouse-toolbar__status" role="alert">
                                                {refreshError}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="search-warehouse-toolbar__right">
                                    {hasActiveSession && (
                                        <>
                                            {activeTabHasHistory && (
                                                <div className="search-warehouse-toolbar__history-wrap">
                                                    {canRefreshSelectedHistoryItem && (
                                                        <button
                                                            type="button"
                                                            className={`search-warehouse-toolbar__history-icon-button${refreshInFlight ? " is-loading" : ""}`}
                                                            onClick={handleRefreshSelectedResult}
                                                            disabled={refreshInFlight}
                                                            aria-label="Refresh Results"
                                                            title={refreshInFlight ? "Refreshing results" : "Refresh Results"}
                                                        >
                                                            <IconControl
                                                                iconKey="refresh"
                                                                className="search-warehouse-toolbar__history-refresh"
                                                            />
                                                        </button>
                                                    )}
                                                    <select
                                                        className="search-warehouse-toolbar__history"
                                                        value={tabHistorySelection[activeOutputTabKey] || ""}
                                                        onChange={(event) => handleHistorySelection(activeOutputTabKey, event.target.value)}
                                                        title="Select previous output item for this tab"
                                                    >
                                                        {(tabHistoryOptions[activeOutputTabKey] || []).map((option) => (
                                                            <option key={`${activeOutputTabKey}-${option.value}`} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <div className="search-warehouse-toolbar__history-icons">
                                                        <div
                                                            className="search-warehouse-toolbar__history-help-wrap"
                                                            onMouseEnter={() => setShowHistoryHelp(true)}
                                                            onMouseLeave={() => setShowHistoryHelp(false)}
                                                            onFocus={() => setShowHistoryHelp(true)}
                                                            onBlur={() => setShowHistoryHelp(false)}
                                                            tabIndex={0}
                                                            aria-label="Selected item message"
                                                        >
                                                            <IconControl
                                                                iconKey={"info"}
                                                                className="search-warehouse-toolbar__history-help"
                                                            />
                                                        </div>
                                                        {showHistoryHelp && (
                                                            <div className="search-warehouse-toolbar__history-help-popover" role="tooltip">
                                                                {getSelectedHistoryHelpText(activeOutputTabKey)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            <div className="search-warehouse-toolbar__tabs-stack">
                                                <TabPaneControl
                                                    className="ai-panel-tabs search-warehouse-toolbar__tabs"
                                                    tabs={[
                                                        { key: "result", title: "Result", content: null },
                                                        { key: "data", title: "Table", content: null }
                                                    ]}
                                                    activeKey={activeOutputTabKey}
                                                    onChange={(_, __, nextKey) => switchTab(nextKey)}
                                                    align="right"
                                                    compact
                                                    framed={false}
                                                    idPrefix="search-warehouse-output-tabs"
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div ref={outputPaneRef} className="search-warehouse-output">
                            <div className={`search-warehouse-main ${showTable ? "search-warehouse-main--data" : ""}`}>
                            {showTable && (
                                <UnmanagedTableControl
                                    rows={rows}
                                    className={"search-object-table"}
                                    metadata={uiMetadata}
                                    loading={tableLoading}
                                    {...unmanagedTableOptions}
                                />
                            )}
                            {hasRenderContent && (
                                <div className="search-warehouse-render">
                                    <div className="search-warehouse-render__surface">
                                        {renderResultSurfaceContent()}
                                    </div>
                                </div>
                            )}
                            {hasChartContent && (
                                <div className="search-warehouse-chart">
                                    {(() => {
                                        const chartOption = resolveChartOption(chartPayload);
                                        if (!chartOption) {
                                            return (
                                                <div className="search-warehouse-state">
                                                    <div className="search-warehouse-state__title">Invalid chart payload</div>
                                                    <div className="search-warehouse-state__subtitle">
                                                        We received a chart command, but no valid ECharts option was found in `value`.
                                                    </div>
                                                </div>
                                            );
                                        }

                                        return (
                                            <div className="search-warehouse-chart__surface">
                                                <ReactECharts
                                                    ref={chartRef}
                                                    option={chartOption}
                                                    notMerge={true}
                                                    lazyUpdate={true}
                                                    style={{ width: "100%", height: "100%" }}
                                                />
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                            {showLoadingState && (
                                <div className="search-warehouse-state">
                                    <div className="search-warehouse-state__title">Loading warehouse data...</div>
                                    <div className="search-warehouse-state__subtitle">
                                        We are searching your warehouse now.
                                    </div>
                                </div>
                            )}
                            {showWarehouseNoResultsState && (
                                <div className="search-warehouse-state">
                                    <div className="search-warehouse-state__title">No warehouse data found</div>
                                    <div className="search-warehouse-state__subtitle">
                                        {emptyStateMessages.warehouseNoResults}
                                    </div>
                                </div>
                            )}
                            {showChatFallbackState && (
                                <div className="search-warehouse-state">
                                    <div className="search-warehouse-state__title">Nothing to display yet</div>
                                    <div className="search-warehouse-state__subtitle">
                                        You are currently having a conversation with the AI. When your request returns displayable results, they will appear here.
                                    </div>
                                </div>
                            )}
                            {showNoDataState && (
                                <div className="search-warehouse-state">
                                    <div className="search-warehouse-state__title">No warehouse data found</div>
                                    <div className="search-warehouse-state__subtitle">
                                        {emptyStateMessages.data}
                                    </div>
                                </div>
                            )}
                            {showNoRenderState && (
                                <div className="search-warehouse-state">
                                    <div className="search-warehouse-state__title">No result available</div>
                                    <div className="search-warehouse-state__subtitle">
                                        {emptyStateMessages.render}
                                    </div>
                                </div>
                            )}
                            {showNoChartState && (
                                <div className="search-warehouse-state">
                                    <div className="search-warehouse-state__title">No chart available</div>
                                    <div className="search-warehouse-state__subtitle">
                                        {emptyStateMessages.chart}
                                    </div>
                                </div>
                            )}
                            </div>
                            </div>
                    </div>
                </AIPanel> 
           
        </>
    );
}

export default SearchWarehouse;

